import {
	FixtureCounterpartyTypes,
	FixtureTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';

type LinkEntry = {
	path: string;
	get: (...params: any[]) => string;
};

type LinkMap = { [key: string]: LinkEntry | LinkMap };

// When creating a new root page, add it here - that way we can find it when adding video guides
export const RootPages = [
	'Accruals',
	'Accounting Mapping',
	'Admin',
	'Brokers',
	'Cargos',
	'Charterers',
	'Company Settings',
	'Contract - Cargo Relet',
	'Contract - TC',
	'Contract - VC',
	'Contracts',
	'Counterparties',
	'Estimate - TC',
	'Estimate - VC',
	'Estimates - Out',
	'Estimate Comparison',
	'Home',
	'Management',
	'Organizations',
	'Owners',
	'Performance',
	'Postings',
	'Previous Period Closes',
	'Suppliers',
	'Owners',
	'Management',
	'Team Members',
	'Team Members Groups',
	'Performance',
	'Video Guides',
	'Voyages',
	'Vessels',
	'World Map',
] as const;

export const Links = {
	Home: {
		path: '/',
		get: () => '/',
	},
	Organization: {
		path: '/admin/organizations/:id',
		get: (orgId: number) => `/admin/organizations/${orgId}`,
	},
	Organizations: {
		path: '/admin/organizations',
		get: () => '/admin/organizations',
	},
	VideoGuides: {
		path: '/admin/video-guides',
		get: () => '/admin/video-guides',
	},
	Management: {
		Approvals: {
			path: '/management/approvals',
			get: () => '/management/approvals',
		},
		Settings: {
			path: '/management/settings',
			get: () => '/management/settings',
		},
	},
	Vessel: {
		path: '/vessels/:id',
		get: (vesselId: number) => `/vessels/${vesselId}`,
	},
	Vessels: {
		path: '/vessels',
		get: () => '/vessels',
	},
	Cargos: {
		path: '/cargo',
		get: () => '/cargo',
	},
	Accounting: {
		Mapping: {
			path: '/accounting/mapping',
			get: () => '/accounting/mapping',
		},
		Accruals: {
			path: '/accounting/accruals',
			get: () => '/accounting/accruals',
		},
		PeriodClose: {
			path: '/accounting/period-close',
			get: () => '/accounting/period-close',
		},
		PreviousCloses: {
			path: '/accounting/previous-closes',
			get: () => '/accounting/previous-closes',
		},
		Export: {
			path: '/accounting/export',
			get: () => '/accounting/export',
		},
	},
	AccountingItems: {
		path: '/ar-ap',
		get: () => '/ar-ap',
	},
	Estimator: {
		path: '/estimator/:groupId/:id',
		get: (
			estimateGroupId: number,
			estimateId: number,
		) => `/estimator/${estimateGroupId}/${estimateId}`,
	},
	Fixtures: {
		[FixtureTypes.BB_OUT]: {
			List: {
				path: '/estimates/bb-out',
				get: () => '/estimates/bb-out',
			},
			Details: {
				path: '/estimates/bb-out/:id',
				get: (fixtureId: number) => `/estimates/bb-out/${fixtureId}`,
			},
		},
		[FixtureTypes.TC_IN]: {
			List: {
				path: '/estimates/tc-in',
				get: () => '/estimates/tc-in',
			},
			Details: {
				path: '/estimates/tc-in/:id',
				get: (fixtureId: number) => `/estimates/tc-in/${fixtureId}`,
			},
		},
		out: {
			List: {
				path: '/estimates/out',
				get: () => '/estimates/out',
			},
		},
		[FixtureTypes.TC_OUT]: {
			List: {
				path: '/estimator/tc-out',
				get: () => '/estimates/tc-out',
			},
			Details: {
				path: '/estimates/tc-out/:id',
				get: (fixtureId: number) => `/estimates/tc-out/${fixtureId}`,
			},
		},
		[FixtureTypes.SPOT]: {
			List: {
				path: '/estimates/spot',
				get: () => '/estimates/spot',
			},
			Details: {
				path: '/estimates/spot/:groupId/:id',
				get: (
					estimateGroupId: number,
					estimateId: number,
				) => `/estimates/spot/${estimateGroupId}/${estimateId}`,
			},
		},
	},
	Expenses: {
		path: '/expenses',
		get: () => '/expenses',
	},
	Cargo: {
		path: '/cargos/:id',
		get: (cargoId: number) => `/cargos/${cargoId}`,
		Intake: {
			path: '/cargos/:id/intake',
			get: (cargoId: number) => `/cargos/${cargoId}/intake`,
		},
	},
	ExpensesApproval: {
		path: '/management/approvals',
		get: () => '/management/approvals',
	},
	VoyageInvoice: {
		path: '/contracts/:voyageId/voyage-invoices/:id',
		get: (voyageId: number, invoiceId: number) => `/contracts/${voyageId}/voyage-invoices/${invoiceId}`,
	},
	Voyage: {
		path: '/contracts/:id',
		get: (voyageId: number) => `/contracts/${voyageId}`,
		Expenses: {
			path: '/contracts/:id/#/expenses',
			get: (voyageId: number) => `/contracts/${voyageId}/#/expenses`,
		},
	},
	Voyages: {
		path: '/contracts',
		get: (filters?: Values<typeof FixtureTypes> | Array<Values<typeof FixtureTypes>>) => {
			// Since we support both TC-Out and VC-Out types as one link
			let list: Array<Values<typeof FixtureTypes>> = [];

			if (filters != null) {
				list = Array.isArray(filters) ? filters : [filters];
			}

			return `/contracts${list.length ? `?filter=${list.join(',')}` : ''}`;
		},
	},
	PerformanceOverview: {
		path: '/performance',
		get: () => '/performance',
	},
	PerformanceDetails: {
		path: '/performance/:id',
		get: (vesselId: number) => `/performance/${vesselId}`,
	},
	Ops: {
		path: '/ops',
		get: () => '/ops',
	},
	OpsDetails: {
		path: '/ops/:vesselId',
		get: (vesselId: number) => `/ops/${vesselId}`,
	},
	Itinerary: {
		path: '/contracts/:voyageId/#/itinerary',
		get: (voyageId: number) => `/contracts/${voyageId}/#/itinerary`,
	},
	Inbox: {
		path: '/inbox',
		get: () => '/inbox',
	},
	Charterer: {
		path: '/charterers/:id',
		get: (chartererId: number) => `/charterers/${chartererId}`,
	},
	Charterers: {
		path: '/charterers',
		get: () => '/charterers',
	},
	Counterparty: {
		path: '/counterparties/:id/:type',
		get: (counterpartyId: number, type: Values<typeof FixtureCounterpartyTypes>) => {
			return `/counterparties/${counterpartyId}/${encodeURIComponent(type)}`;
		},
	},
	Counterparties: {
		path: '/counterparties',
		get: () => '/counterparties',
	},
	Map: {
		path: '/map',
		get: (vesselId?: number) => `/map${vesselId != null ? `?v=${vesselId}` : ''}`,
	},
	Owner: {
		path: '/owners/:id',
		get: (ownerId: number) => `/owners/${ownerId}`,
	},
	Owners: {
		path: '/owners',
		get: () => '/owners',
	},
	Supplier: {
		path: '/suppliers/:id',
		get: (supplierId: number) => `/suppliers/${supplierId}`,
	},
	Suppliers: {
		path: '/suppliers',
		get: () => '/suppliers',
	},
	Broker: {
		path: '/brokers/:id',
		get: (brokerId: number) => `/brokers/${brokerId}`,
	},
	Brokers: {
		path: '/brokers',
		get: () => '/brokers',
	},
	User: {
		path: '/settings/users/:id',
		get: (userId: number) => `/settings/users/${userId}`,
	},
	Users: {
		path: '/settings/users/',
		get: () => '/settings/users/',
	},
	Group: {
		path: '/settings/groups/:id',
		get: (groupId: number) => `/settings/groups/${groupId}`,
	},
	Groups: {
		path: '/settings/groups/',
		get: () => '/settings/groups/',
	},
	AuditTrail: {
		path: '/audit-trail',
		get: () => '/audit-trail',
	},
	Development: {
		path: '/dev',
		get: () => '/dev',
	},
};

// If this has a type error, that means an entry in Links has the wrong type.
// We do it this way instead of typing it directly to preserve get function parameters.
// Otherwise, they would just show up as "(...any[]) => string" when used.
const _linksTypeCheck: LinkMap = Links;
